import { parse, stringify } from 'qs'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export type Params = Record<string, unknown>

type UseParsedSearchParamsConfig<TData extends Params> = {
  /**
   * A helper method to validate and default the raw URL params for the page.
   * This is often just a thin wrapper around a zod schema's `parse` method,
   * but we have this as a prop to allow for more complex validation logic and
   * more easily unit test the validators.
   */
  validateParams: (rawParams: Params) => TData
}

const useParsedSearchParams = <TData extends Params>({
  validateParams,
}: UseParsedSearchParamsConfig<TData>) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const updateSearchParams = useCallback(
    (newParams: Partial<TData>) => {
      const parsedPrevParams = parse(searchParams.toString())
      const stringifiedParams = stringify({
        ...validateParams(parsedPrevParams),
        ...validateParams(newParams),
      })
      setSearchParams(stringifiedParams)
    },
    [searchParams, setSearchParams, validateParams]
  )

  const validSearchParams = useMemo(() => {
    const paramsData = parse(searchParams.toString())
    return validateParams(paramsData)
  }, [searchParams, validateParams])

  return { searchParams: validSearchParams, setSearchParams: updateSearchParams }
}

export default useParsedSearchParams
